import { useState, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, Save, Map, Globe, Shield, Package } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useDungeonStore } from '../../../../stores/dungeonStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { generateWorldContextText, generateCharacterContext, generateEnemyContext, generateItemContext, generateDungeonContext, generateCommunityContext, generateSocietyContext } from '@creanizer/utils'
import type { DungeonInput, DungeonResult, SavedDungeon } from '@creanizer/types'
import { DungeonForm } from './DungeonForm'
import { DungeonResult as DungeonResultComponent } from './DungeonResult'
import { DungeonSaved } from './DungeonSaved'
import { 
  handleDungeonGeneratorError, 
  salvagePartialDungeon, 
  estimateDungeonMemory,
  countFloorsInPartialData,
  validateFloorConnections,
  validateDungeonStructure 
} from './utils'

export function DungeonGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { dungeons, addDungeon, addBatchDungeons } = useDungeonStore()
  const { currentWorld, savedWorlds } = useWorldBuilderStore()
  const { characters } = useCharacterStore()
  const { enemies } = useEnemyStore()
  const { items } = useItemStore()
  const { savedCommunities } = useCommunityStore()
  const { savedSocieties } = useSocietyStore()
  const generator = useGenerator('dungeon')
  const streamGenerator = useStreamGenerator('dungeon')
  const [activeTab, setActiveTab] = useState('generate')
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>('')
  const [selectedEnemyId, setSelectedEnemyId] = useState<string>('')
  const [selectedItemId, setSelectedItemId] = useState<string>('')
  const [selectedCommunityId, setSelectedCommunityId] = useState<string>('')
  const [selectedSocietyId, setSelectedSocietyId] = useState<string>('')
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [isSaved, setIsSaved] = useState<boolean>(false)
  
  const [input, setInput] = useState<DungeonInput>({
    mode: 'standard',
    difficulty: 'normal',
    floors: 3,
    roomsPerFloor: 5,
    layout: 'branching',
    size: 'medium',
    lightLevel: 'dim',
    condition: 'deteriorating',
    trapDensity: 'medium',
    enemyDensity: 'medium',
    treasureRichness: 'average',
    puzzleComplexity: 'moderate',
    creativityLevel: 7,
    numberOfDungeons: 3,
    batchConnection: 'none',
    // 世界観連携（currentWorldから自動設定）
    worldContext: currentWorld ? generateWorldContextText(currentWorld.result) : undefined
  })
  
  const [results, setResults] = useState<DungeonResult[]>([])
  const [error, setError] = useState<string>('')
  const [currentDungeonId, setCurrentDungeonId] = useState<string | undefined>()
  // const [currentMapData, setCurrentMapData] = useState<Record<number, any> | undefined>() // マップ機能一時非表示
  const resultRef = useRef<HTMLDivElement>(null)
  
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }
    
    setError('')
    setResults([])
    setIsSaved(false)
    setCurrentDungeonId(undefined)
    // setCurrentMapData(undefined) // マップ機能一時非表示
    
    // デバッグログ（開発環境のみ）
    if (process.env.NODE_ENV === 'development') {
      console.log('[DungeonGenerator] Generating with input:', {
        mode: input.mode,
        floors: input.floors,
        roomsPerFloor: input.roomsPerFloor,
        name: input.name,
        worldContext: input.worldContext ? 'Set' : 'Not set',
        communityContext: input.communityContext ? 'Set' : 'Not set',
        societyContext: input.societyContext ? 'Set' : 'Not set'
      })
    }
    
    try {
      if (useStreaming) {
        await streamGenerator.generateStream(input, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              const rawResults = Array.isArray(resultData) ? resultData : [resultData]
              
              // デバッグログ（開発環境のみ）
              if (process.env.NODE_ENV === 'development') {
                console.log('[Dungeon Generation] Processing', rawResults.length, 'dungeon(s)')
              }
              
              // 部分データ救済を試みる
              const validResults = rawResults.map(dungeon => {
                if (dungeon && dungeon.basicInfo && dungeon.basicInfo.name) {
                  // 構造検証
                  if (validateDungeonStructure(dungeon)) {
                    // フロア接続検証
                    const connectionErrors = validateFloorConnections(dungeon)
                    if (connectionErrors.length > 0 && process.env.NODE_ENV === 'development') {
                      console.warn('[Floor Connection] Warning:', connectionErrors)
                    }
                    
                    // メモリ使用量を推定してログ出力
                    const memoryEstimate = estimateDungeonMemory(dungeon)
                    if (process.env.NODE_ENV === 'development') {
                      console.log(`[Memory] Dungeon "${dungeon.basicInfo.name}" estimated size: ${memoryEstimate} bytes`)
                    }
                    return dungeon
                  }
                }
                // 不完全なデータの救済を試みる
                const salvaged = salvagePartialDungeon(dungeon)
                if (salvaged) {
                  console.log('[Partial Dungeon Recovery] Salvaged partial dungeon data')
                  return salvaged
                }
                return null
              }).filter(Boolean)
              
              if (validResults.length === 0) {
                console.error('No valid dungeons in results')
                setError('有効なダンジョンデータが取得できませんでした。')
                return
              }
              
              setResults(validResults as DungeonResult[])
              
              // バッチモードで期待より少ない結果が返された場合
              if (input.mode === 'batch' && Array.isArray(resultData)) {
                const expectedCount = input.numberOfDungeons || 3
                if (validResults.length > 0 && validResults.length < expectedCount) {
                  console.warn(`[Partial Dungeon Recovery] Recovered ${validResults.length}/${expectedCount} dungeons`)
                  setError(`${validResults.length}個のみ取得できました（${expectedCount}個中）`)
                }
              }
              
              setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 100)
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            const errorMessage = handleDungeonGeneratorError(error, input)
            setError(errorMessage)
          }
        })
      } else {
        const response = await generator.mutateAsync(input)
        const resultData = response.result || response
        
        if (!resultData) {
          throw new Error('生成結果が空です。APIが混雑している可能性があります。')
        }
        
        const rawResults = Array.isArray(resultData) ? resultData : [resultData]
        
        // デバッグログ（開発環境のみ）
        if (process.env.NODE_ENV === 'development') {
          console.log('[Dungeon Generation] Processing', rawResults.length, 'dungeon(s)')
        }
        
        const validResults = rawResults.filter(dungeon => {
          if (dungeon && dungeon.basicInfo && dungeon.basicInfo.name) {
            // 構造検証
            if (validateDungeonStructure(dungeon)) {
              // フロア接続検証
              const connectionErrors = validateFloorConnections(dungeon)
              if (connectionErrors.length > 0 && process.env.NODE_ENV === 'development') {
                console.warn('[Floor Connection] Warning:', connectionErrors)
              }
              
              // メモリ使用量を推定してログ出力
              const memoryEstimate = estimateDungeonMemory(dungeon)
              if (process.env.NODE_ENV === 'development') {
                console.log(`[Memory] Dungeon "${dungeon.basicInfo.name}" estimated size: ${memoryEstimate} bytes`)
              }
              return true
            }
          }
          return false
        })
        
        if (validResults.length === 0) {
          throw new Error('有効なダンジョンデータが生成されませんでした。')
        }
        
        setResults(validResults as DungeonResult[])
        
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('不明なエラーが発生しました')
      const errorMessage = handleDungeonGeneratorError(error, input)
      setError(errorMessage)
      console.error('Generation error:', err)
    }
  }
  
  const handleSave = () => {
    if (results.length === 0) return
    
    // 新しいダンジョンを保存してIDを生成
    const newDungeonId = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    
    if (input.mode === 'batch') {
      addBatchDungeons(input, results)
    } else {
      results.forEach(result => {
        addDungeon(input, result)
      })
    }
    
    // 保存後、現在のダンジョンIDを設定（単一モードの場合のみ）
    if (input.mode !== 'batch' && results.length === 1) {
      setCurrentDungeonId(newDungeonId)
    }
    
    setIsSaved(true)
  }
  
  const handleLoad = (dungeon: SavedDungeon) => {
    // デバッグログ（開発環境のみ）
    if (process.env.NODE_ENV === 'development') {
      console.log('[DungeonGenerator] Loading dungeon:', {
        savedFloors: dungeon.input.floors,
        savedRoomsPerFloor: dungeon.input.roomsPerFloor,
        savedMode: dungeon.input.mode,
        savedName: dungeon.input.name
      })
    }
    
    // 保存されたダンジョンの入力パラメータと結果を設定
    // デフォルト値と明示的にマージして確実に上書き
    const loadedInput: DungeonInput = {
      ...dungeon.input,
      // 保存時に値が存在していた場合は確実にその値を使用
      floors: dungeon.input.floors !== undefined ? dungeon.input.floors : 3,
      roomsPerFloor: dungeon.input.roomsPerFloor !== undefined ? dungeon.input.roomsPerFloor : 5,
      mode: dungeon.input.mode || 'standard'
    }
    
    setInput(loadedInput)
    setResults([dungeon.result])
    setIsSaved(true)
    setCurrentDungeonId(dungeon.id)
    // setCurrentMapData(dungeon.mapData) // マップ機能一時非表示
    setActiveTab('generate')
    // 結果エリアにスクロール
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-lg">
        <div className="flex items-center space-x-3 mb-2">
          <Map className="h-8 w-8" />
          <h1 className="text-3xl font-bold">ダンジョンジェネレータ</h1>
        </div>
        <p className="text-lg opacity-90">
          RPGゲームやTRPG用の詳細なダンジョン構造を自動生成します
        </p>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({dungeons.length}/200)
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-6">
          {!isApiKeySet() && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>APIキーが未設定です</AlertTitle>
              <AlertDescription>
                設定ページからGemini APIキーを設定してください。
              </AlertDescription>
            </Alert>
          )}
          
          <DungeonForm
            input={input}
            onChange={setInput}
            savedWorlds={savedWorlds}
            currentWorld={currentWorld}
            characters={characters}
            enemies={enemies}
            items={items}
            communities={savedCommunities}
            societies={savedSocieties}
            selectedWorldId={selectedWorldId}
            setSelectedWorldId={setSelectedWorldId}
            selectedCharacterId={selectedCharacterId}
            setSelectedCharacterId={setSelectedCharacterId}
            selectedEnemyId={selectedEnemyId}
            setSelectedEnemyId={setSelectedEnemyId}
            selectedItemId={selectedItemId}
            setSelectedItemId={setSelectedItemId}
            selectedCommunityId={selectedCommunityId}
            setSelectedCommunityId={setSelectedCommunityId}
            selectedSocietyId={selectedSocietyId}
            setSelectedSocietyId={setSelectedSocietyId}
            useStreaming={useStreaming}
            onStreamingChange={setUseStreaming}
          />
          
          <div className="flex space-x-4">
            <Button 
              onClick={handleGenerate}
              disabled={!isApiKeySet() || generator.isPending || streamGenerator.isStreaming}
              className="flex-1"
            >
              {generator.isPending || streamGenerator.isStreaming ? '生成中...' : 'ダンジョンを生成'}
            </Button>
            
            {results.length > 0 && (
              isSaved ? (
                <Button variant="outline" disabled>
                  <Save className="mr-2 h-4 w-4" />
                  保存済み
                </Button>
              ) : (
                <Button onClick={handleSave} variant="outline">
                  <Save className="mr-2 h-4 w-4" />
                  保存
                </Button>
              )
            )}
          </div>
          
          {(generator.isPending || streamGenerator.isStreaming) && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>生成中...</span>
                <span>{Math.round(streamGenerator.progress)}%</span>
              </div>
              <Progress value={streamGenerator.progress} />
              {input.mode === 'batch' && (
                <p className="text-sm text-muted-foreground">
                  バッチモードで{input.numberOfDungeons}個のダンジョンを生成中...
                </p>
              )}
              {input.floors && input.floors > 3 && (
                <p className="text-sm text-muted-foreground">
                  {input.floors}フロアの大規模ダンジョンを構築中...
                </p>
              )}
            </div>
          )}
          
          {streamGenerator.partialContent && (
            <div className="space-y-2">
              <div className="max-h-48 overflow-y-auto border rounded p-2">
                <pre className="text-xs whitespace-pre-wrap">
                  {streamGenerator.partialContent.slice(-500)}
                  {streamGenerator.partialContent.length > 500 && '...'}
                </pre>
              </div>
              <div className="text-sm text-muted-foreground">
                処理中のフロア: {countFloorsInPartialData(streamGenerator.partialContent)}
              </div>
            </div>
          )}
          
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div ref={resultRef}>
            {results.length > 0 && (
              <DungeonResultComponent 
                results={results} 
                mode={input.mode || 'standard'}
                dungeonId={currentDungeonId}
              />
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="saved">
          <DungeonSaved
            dungeons={dungeons}
            onLoad={handleLoad}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}